import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import Sidebar from '../../../components/Sidebar';
import Settings from '../../../Settings';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { FaPlus, FaSpinner, FaTrash, FaPaperclip, FaPaperPlane, FaDatabase, FaExternalLinkAlt } from 'react-icons/fa';
import { TbLayoutSidebarLeftCollapse, TbLayoutSidebarRightCollapse } from 'react-icons/tb';
import { FormControl, FormControlLabel, RadioGroup, Radio, Popover, Button as MUIButton} from '@mui/material';
import veloxIcon from '../../../assets/Velox-icon-color.png';
import { sendMessage, getChatMessages, deleteChat } from '../../../services/chat';
import { useVirtualChatHistory } from '../../../hooks/useVirtualChatHistory';
import VirtualScrollList from '../../../components/VirtualScrollList';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { fileToBase64 } from '../../../utils/fileHelpers';
import AgentVChatOverlay from '../../../components/wizard/AgentVChatOverlay';
import ChartDisplay from '../../../components/wizard/ChartDisplay';
import useWizardCharts from '../../../hooks/useWizardCharts';
import SlackAction from '../../../components/SlackAction';

// Module Pages
import CompassView from './CompassView';
import KnowledgeHub from './KnowledgeHub';
import Radar from './Radar';
import WizardView from './WizardView';

// App styles
import '../../../App.css';
import './Wizard.css';

// Team Plan Dashboard
function AgentVTeamDashboard() {
  const { logout } = useAuth();
  
  const handleLogout = () => {
    logout();
  };

  return (
    <div className="app-root">
      <Sidebar 
        onLogout={handleLogout} 
      />
      <main className="main-content">
        
        <Routes>
          <Route index element={<Navigate to="agent-v" replace />} />
          <Route path="agent-v" element={<AgentVTeamAssistant />} />
          <Route path="compass" element={<CompassView />} />
          <Route path="compass/*" element={<CompassView />} />
          <Route path="knowledge" element={<Navigate to="knowledge-hub" replace />} />
          <Route path="knowledge-hub" element={<KnowledgeHub />} />
          <Route path="knowledge-hub/*" element={<KnowledgeHub />} />
          <Route path="radar/*" element={<Radar />} />
          <Route path="wizard/*" element={<WizardView />} />
          <Route path="settings" element={<Settings />} />
          {/* Remove the problematic catch-all route that was causing redirects */}
        </Routes>
      </main>
    </div>
  );
}

// Team Plan Agent V
function AgentVTeamAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [selectedChat, setSelectedChat] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isChartOverlayOpen, setIsChartOverlayOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState(null);
  const { showError } = useToast();
  
  // Add new state for pagination and caching
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const messagesCache = useRef(new Map());
  const lastLoadedChatId = useRef(null); // Track which chat we last loaded messages for
  const PAGE_SIZE = 20;

  const MESSAGE_CACHE_TIME = 2 * 60 * 1000; // 2 minutes

  // Add new state for chat loading
  const [isChatLoading, setIsChatLoading] = useState(false);

  // Add state for communication style, persisted in sessionStorage
  const getInitialCommStyle = () => {
    const savedStyle = sessionStorage.getItem('commStyle');
    return savedStyle === 'casual' ? 'casual' : 'professional';
  };
  const [commStyle, setCommStyle] = useState(getInitialCommStyle());
  const [commAnchorEl, setCommAnchorEl] = useState(null);
  const {
    charts,
    loadingCharts,
    loadCharts,
    handleChartGenerated,
    handleChartDelete,
    handleChartUpdate
  } = useWizardCharts({ startInLoadingState: true });

  // Use virtual chat history hook
  const {
    allChats: chatHistory,
    isLoading: isLoadingHistory,
    isLoadingMore: isLoadingMoreChats,
    error: historyError,
    hasMore: hasMoreChats,
    loadInitialChatHistory,
    loadMoreChats,
    addNewChat,
    removeChat
  } = useVirtualChatHistory(user?.id);

  useEffect(() => {
    sessionStorage.setItem('commStyle', commStyle);
  }, [commStyle]);

  // Show toast notification when history error occurs
  useEffect(() => {
    if (historyError) {
      showError(historyError);
    }
  }, [historyError, showError]);

  const commOptions = [
    { value: 'professional', label: 'Professional - Formal and business-oriented' },
    { value: 'casual', label: 'Casual - Friendly and conversational' },
  ];

  // Add function to check if cache is valid for messages
  const isCacheValid = (timestamp, cacheTime) => {
    return timestamp && (Date.now() - timestamp < cacheTime);
  };

  // Update loadMoreMessages with better caching
  const loadMoreMessages = useCallback(async () => {
    if (!selectedChat || isLoadingMore || !hasMoreMessages) return;
    if (messages.length === 0) return; // Don't try to load more if there are no messages
    setIsLoadingMore(true);
    try {
      const oldestMessageTimestamp = messages.length > 0 
        ? messages[messages.length - 1].timestamp 
        : new Date().toISOString();
      const cacheKey = `${selectedChat.id}-${oldestMessageTimestamp}`;
      const cachedData = messagesCache.current.get(cacheKey);
      if (cachedData && isCacheValid(cachedData.timestamp, MESSAGE_CACHE_TIME)) {
        setMessages(prev => [...prev, ...cachedData.data]);
        setHasMoreMessages(cachedData.data.length === PAGE_SIZE);
        setIsLoadingMore(false);
        return;
      }
      const response = await getChatMessages(selectedChat.id, {
        before: oldestMessageTimestamp,
        limit: PAGE_SIZE
      });
      if (Array.isArray(response)) {
        const newMessages = response;
        messagesCache.current.set(cacheKey, {
          data: newMessages,
          timestamp: Date.now()
        });
        setMessages(prev => [...prev, ...newMessages]);
        setHasMoreMessages(newMessages.length === PAGE_SIZE);
      } else {
        setHasMoreMessages(false);
      }
    } catch (error) {
      // Only show error if user explicitly tried to load more (not on initial load or empty)
      if (messages.length > 0) {
        showError('Failed to load more messages. Please try again.');
      }
    } finally {
      setIsLoadingMore(false);
    }
  }, [selectedChat, messages, isLoadingMore, hasMoreMessages, MESSAGE_CACHE_TIME]);

  // Load initial chat history
  useEffect(() => {
    if (user) {
      loadInitialChatHistory();
    }
  }, [user, loadInitialChatHistory]);

  useEffect(() => {
    loadCharts();
  }, [loadCharts]);

  // Add scroll handler for infinite loading
  const handleScroll = useCallback(() => {
    if (!chatContainerRef.current) return;
    if (!hasMoreMessages || isLoadingMore) return; // Prevent auto-load if no more messages or already loading
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    // Load more when user scrolls to 80% of the content
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      loadMoreMessages();
    }
  }, [isLoadingMore, hasMoreMessages, loadMoreMessages]);

  // Add scroll event listener
  useEffect(() => {
    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Update message loading logic with caching
  useEffect(() => {
    if (selectedChat && selectedChat.id) {
      // Don't load if we already loaded messages for this chat
      if (lastLoadedChatId.current === selectedChat.id) {
        // We already have messages for this chat, don't reload
        return;
      }
      
      // Don't load messages if we already have messages and they're recent (active conversation)
      if (messages.length > 0) {
        const mostRecentMessage = messages[messages.length - 1];
        if (mostRecentMessage && mostRecentMessage.timestamp) {
          const messageTime = new Date(mostRecentMessage.timestamp).getTime();
          const now = Date.now();
          // If messages are very recent (within 5 seconds), we're in an active conversation
          // Don't show loading, just continue with existing messages
          if (now - messageTime < 5000) {
            lastLoadedChatId.current = selectedChat.id;
            return; // Skip loading, we already have the messages
          }
        }
      }
      
      const loadMessages = async () => {
        setIsChatLoading(true);
        try {
          const cacheKey = `${selectedChat.id}-initial`;
          const cachedMessages = messagesCache.current.get(cacheKey);
          if (cachedMessages) {
            setMessages(cachedMessages);
            lastLoadedChatId.current = selectedChat.id;
            setIsChatLoading(false);
            return;
          }
          const response = await getChatMessages(selectedChat.id, { limit: PAGE_SIZE });
          if (Array.isArray(response)) {
            messagesCache.current.set(cacheKey, response);
            setMessages(response);
            lastLoadedChatId.current = selectedChat.id;
            setHasMoreMessages(response.length === PAGE_SIZE);
          } else {
            setMessages([]);
            setHasMoreMessages(false);
            lastLoadedChatId.current = selectedChat.id;
          }
        } catch (error) {
          showError('Failed to load messages');
          setMessages([]);
          setHasMoreMessages(false);
          lastLoadedChatId.current = selectedChat.id;
        } finally {
          setIsChatLoading(false);
        }
      };
      
      // Only clear messages if we're switching to a different chat
      if (lastLoadedChatId.current && lastLoadedChatId.current !== selectedChat.id) {
        setMessages([]);
      }
      setHasMoreMessages(true);
      loadMessages();
    } else if (selectedChat === null) {
      // Clear messages when no chat is selected
      setMessages([]);
      setHasMoreMessages(false);
      lastLoadedChatId.current = null;
    }
  }, [selectedChat?.id]);

  // Chat history starts collapsed on load
  // Removed useEffect that was setting it to expanded based on screen width

  // Add window resize handler
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setShowHistory(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const createNewChat = () => {
    setSelectedChat(null);
    setMessages([]);
    setAttachments([]);
    lastLoadedChatId.current = null; // Reset when creating new chat
  };

  const handleFileAttachment = (event) => {
    const files = Array.from(event.target.files);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() && attachments.length === 0) return;

    const displayAttachments = attachments.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type
    }));

    const preparedAttachments = await Promise.all(
      attachments.map(async (file) => ({
        name: file.name,
        type: file.type || 'application/octet-stream',
        size: file.size,
        content: await fileToBase64(file)
      }))
    );

    const userMessage = {
      content: input,
      type: 'user',
      attachments: displayAttachments,
      timestamp: new Date().toISOString(),
      status: 'sending'
    };

    const currentInput = input;
    setInput('');
    setAttachments([]);
    setIsLoading(true);

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    const streamingTimestamp = new Date().toISOString();
    setStreamingMessage({
      content: '',
      type: 'assistant',
      timestamp: streamingTimestamp,
      status: 'streaming'
    });

    try {
      const response = await sendMessage(currentInput, selectedChat?.id, commStyle, {
        stream: true,
        attachments: preparedAttachments,
        onToken: (token) => {
          setStreamingMessage(prev => (
            prev ? { ...prev, content: `${prev.content || ''}${token}` } : prev
          ));
        }
      });
      // Handle both streaming and non-streaming responses
      // Streaming returns result directly, non-streaming returns { data: { ... } }
      const responseData = response.data || response;
      
      if (!responseData || !responseData.chat || !responseData.message) {
        throw new Error('Invalid response structure from server');
      }
      
      const { chat, message, metadata, context } = responseData;
      
      // Update chat history
      if (!selectedChat) {
        addNewChat(chat);
        // Mark this chat as loaded so we don't trigger loading
        lastLoadedChatId.current = chat.id;
        setSelectedChat(chat);
        // Don't trigger message loading for new chats - we already have the messages
      } else {
        // Update existing chat in the list - this will be handled by the hook
        // The hook manages the chat list state
        // Mark as loaded if it's the same chat
        if (selectedChat.id === chat.id) {
          lastLoadedChatId.current = chat.id;
        }
      }

      // Update message status
      setMessages(prev => prev.map(msg => 
        msg.timestamp === userMessage.timestamp && msg.content === userMessage.content
          ? { ...msg, status: 'sent' }
          : msg
      ));

      // Use streaming content if available to ensure continuity
      // This prevents visual refresh by ensuring smooth transition
      const finalMessageContent = streamingMessage?.content && streamingMessage.content.length > 0
        ? streamingMessage.content
        : message.content;

      // Add final message first, then clear streaming message
      // React 18+ automatically batches these updates in event handlers
      // Include metadata for workflow creation
      setMessages(prev => [...prev, { 
        ...message, 
        content: finalMessageContent,
        status: 'sent',
        timestamp: message.timestamp || streamingTimestamp,
        metadata: metadata || null,
        context: context || null
      }]);
      setStreamingMessage(null);
    } catch (error) {
      console.error('Error sending message:', error);
      showError(error.message || 'Failed to send message. Please try again.');
      
      // Update message status to failed (but don't show error in chat)
      setMessages(prev => prev.map(msg => 
        msg.timestamp === userMessage.timestamp && msg.content === userMessage.content
          ? { ...msg, status: 'failed' }
          : msg
      ));
      setStreamingMessage(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteChat = async (chatId) => {
    try {
      await deleteChat(chatId);
      removeChat(chatId);
      if (selectedChat?.id === chatId) {
        setSelectedChat(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
      showError('Failed to delete chat');
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? 'Just now' : date.toLocaleTimeString();
  };

  const renderMessage = (message, index) => {
    const messageKey = message.id || `${message.timestamp || 'pending'}-${message.type || 'message'}-${index}`;
    const messageClass = `chat-message ${message.type} ${message.status === 'sending' ? 'sending' : ''}`;
    return (
      <div key={messageKey} className={messageClass}>
        <div className="message-content">
          {message.content && (
            <div className="message-text">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code({node, inline, className, children, ...props}) {
                    const match = /language-(\w+)/.exec(className || '');
                    return !inline && match ? (
                      <SyntaxHighlighter
                        style={vscDarkPlus}
                        language={match[1]}
                        PreTag="div"
                        {...props}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  }
                }}
              >
                {message.content}
              </ReactMarkdown>
      </div>
          )}
          {message.status === 'streaming' && !message.content && (
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          )}
          
          {message.attachments && message.attachments.length > 0 && (
            <div className="message-attachments">
              {message.attachments.map((attachment, index) => (
                <div key={index} className="attachment-item">
                  <FaPaperclip />
                  <span>{attachment.name}</span>
                  <span className="attachment-size">
                    {(attachment.size / 1024).toFixed(1)}KB
                  </span>
                </div>
              ))}
            </div>
          )}

          {message.sources && message.sources.length > 0 && (
            <div className="message-sources">
              <div className="sources-header">
                <FaDatabase /> Sources:
              </div>
              <ul>
                {message.sources.map((source, index) => (
                  <li key={index}>{source}</li>
                ))}
              </ul>
          </div>
          )}

          {/* Workflow Creation Preview */}
          {message.metadata?.workflowCreation && message.metadata?.customAgentId && (
            <div className="workflow-preview-card" style={{
              marginTop: '16px',
              padding: '16px',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              backgroundColor: '#f9f9f9'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                  Custom Agent Created
                </h4>
                <button
                  onClick={() => navigate(`/app/wizard/customAgents/${message.metadata.customAgentId}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Open in Builder
                  <FaExternalLinkAlt size={12} />
                </button>
              </div>
              {message.context?.workflowCreation?.workflow && (
                <div style={{ fontSize: '14px', color: '#666' }}>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Name:</strong> {message.context.workflowCreation.workflow.name}
                  </p>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Description:</strong> {message.context.workflowCreation.workflow.description}
                  </p>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Nodes:</strong> {message.context.workflowCreation.workflow.nodes.length}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Workflow Clarification Questions - Only show if clarifications exist and are not empty */}
          {message.metadata?.workflowCreation && 
           message.metadata?.requiresClarification && 
           message.context?.workflowCreation?.clarifications && 
           Array.isArray(message.context.workflowCreation.clarifications) &&
           message.context.workflowCreation.clarifications.length > 0 &&
           message.context.workflowCreation.clarifications.some(section => 
             Array.isArray(section.questions) && section.questions.length > 0
           ) && (
            <div className="workflow-clarification-card" style={{
              marginTop: '16px',
              padding: '16px',
              border: '1px solid #ffa500',
              borderRadius: '8px',
              backgroundColor: '#fff9e6'
            }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#ff8c00' }}>
                Additional Information Needed
              </h4>
              {message.context.workflowCreation.clarifications
                .filter(section => Array.isArray(section.questions) && section.questions.length > 0)
                .map((section, sectionIndex) => (
                <div key={sectionIndex} style={{ marginBottom: '16px' }}>
                  <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>
                    {section.title}
                  </h5>
                  <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#666' }}>
                    {section.description}
                  </p>
                  <ul style={{ margin: '0', paddingLeft: '20px' }}>
                    {section.questions.map((q, qIndex) => (
                      <li key={qIndex} style={{ marginBottom: '8px', fontSize: '13px' }}>
                        {q.question}
                        {q.options && Array.isArray(q.options) && q.options.length > 0 && (
                          <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                            {q.options.map((opt, optIndex) => (
                              <li key={optIndex} style={{ fontSize: '12px', color: '#888' }}>
                                {String.fromCharCode(97 + optIndex)}) {opt}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {message.type === 'assistant' && (
            <SlackAction
              messageContent={message.content}
              messageId={message.id}
              hasBeenSent={message.slackSent || false}
              allMessages={messages}
              onSent={(channelName) => {
                // Mark the message as sent
                setMessages(prev => prev.map(msg => 
                  msg.id === message.id || msg.timestamp === message.timestamp
                    ? { ...msg, slackSent: true }
                    : msg
                ));
                // Add a confirmation message
                const confirmationMessage = {
                  content: `Content has been shared to #${channelName} on Slack.`,
                  type: 'assistant',
                  timestamp: new Date().toISOString(),
                  status: 'sent',
                  slackSent: true // Mark confirmation message so button doesn't show
                };
                setMessages(prev => [...prev, confirmationMessage]);
              }}
            />
          )}
          
          <div className="message-timestamp">
            {formatTimestamp(message.timestamp)}
          </div>
        </div>
      </div>
    );
  };

  const renderWelcomeMessage = () => {
    if (messages.length > 0) return null;
    
    return (
      <div className="welcome-overlay">
        <div className="welcome-message">
          <h2>Hi, {user?.firstName || 'there'}!</h2>
          <p>What would you like to do?</p>
        </div>
      </div>
    );
  };

  const toggleHistory = () => {
    setShowHistory(prev => !prev);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  return (
    <div className="agentv-assistant-page">
      <div className="chat-layout">
        <div className="chat-container">
          <div className="chat-header">
            <div className="chat-title" style={{ justifyContent: 'flex-start', marginLeft: 0, textAlign: 'left', flex: 1 }}>
              <img src={veloxIcon} alt="Velox AI" className="chat-icon" />
              <span>Agent V</span>
            </div>
            <div className="chat-header-actions">
              <button 
                className="agentv-create-chart-button"
                onClick={createNewChat}
                aria-label="Start new chat"
              >
                <FaPlus /> New Chat
              </button>
              <button 
                className="toggle-history-button"
                onClick={toggleHistory}
                aria-label={showHistory ? 'Hide chat history' : 'Show chat history'}
              >
                {showHistory ? <TbLayoutSidebarRightCollapse /> : <TbLayoutSidebarLeftCollapse color="#2563eb" />}
              </button>
            </div>
          </div>
  
        <div className="chat-messages" ref={chatContainerRef}>
          {isLoadingMore && (
            <div className="loading-more-messages">
              <FaSpinner className="spinner" /> Loading more messages...
            </div>
          )}
          {isChatLoading && (
            <div className="loading-indicator">
              <FaSpinner className="spinner" /> Loading chat...
            </div>
          )}
          {!isChatLoading && renderWelcomeMessage()}
          {messages.map((message, index) => (
            renderMessage(message, index)
          ))}
          {streamingMessage && renderMessage(streamingMessage, messages.length)}
          {isLoading && !streamingMessage && (
            <div className="message assistant">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
  
        <div className="chat-input-container">
          {attachments.length > 0 && (
            <div className="attachments-preview">
              {attachments.map((file, index) => (
                <div key={index} className="attachment-preview-item">
                  <FaPaperclip />
                  <span>{file.name}</span>
                  <button
                    className="remove-attachment"
                    onClick={() => removeAttachment(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="chat-input-wrapper">
            <button 
              className="chat-attachment-button"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Attach file"
            >
              <FaPaperclip />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileAttachment}
              style={{ display: 'none' }}
              multiple
            />
            <MUIButton
              variant="outlined"
              size="small" 
              sx={{ ml: 1, mr: 1, fontSize: '0.65rem', textTransform: 'none', borderRadius: 2, minWidth: 0, px: 1.5, py: 1 }}
              onClick={e => setCommAnchorEl(e.currentTarget)}
              aria-label="Select communication style"
            >
              {commOptions.find(opt => opt.value === commStyle)?.label.split(' - ')[0]}
            </MUIButton>
            <Popover
              open={Boolean(commAnchorEl)}
              anchorEl={commAnchorEl}
              onClose={() => setCommAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { p: 2, minWidth: 260 } }}
            >
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="communication-style"
                  name="communication-style"
                  value={commStyle}
                  onChange={e => {
                    setCommStyle(e.target.value);
                    setCommAnchorEl(null);
                  }}
                >
                  {commOptions.map(opt => (
                    <FormControlLabel
                      key={opt.value}
                      value={opt.value}
                      control={<Radio size="small" />}
                      label={<span style={{ fontSize: 13 }}>{opt.label}</span>}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Popover>
            <textarea
              className="chat-input-field"
              placeholder="Type your message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              aria-label="Message input"
            />
            <button 
              className="chat-input-send"
              onClick={handleSendMessage}
              disabled={(!input.trim() && attachments.length === 0)}
              aria-label="Send message"
            >
              <FaPaperPlane />
            </button>
          </div>
        </div>
        </div>
  
        {/* Chat History Sidebar */}
        <div className={`chat-history-sidebar ${showHistory ? 'visible' : ''}`}>
        <div className="chat-history-header">
          <h2>Chat History</h2>
        </div>
        <button 
          className="new-chat-button"
          onClick={createNewChat}
        >
          <FaPlus /> New Chat
        </button>
        <div className="chat-history-list">
          {isLoadingHistory ? (
            <div className="loading-indicator">
              <FaSpinner className="spinner" /> Loading previous chats...
            </div>
          ) : historyError ? (
            <div className="no-chats-message">
              {historyError}
            </div>
          ) : !chatHistory || chatHistory.length === 0 ? (
            <div className="no-chats-message">
              No chat history yet. Start a new chat!
            </div>
          ) : (
            <VirtualScrollList
              items={chatHistory}
              itemHeight={100}
              containerHeight={400}
              overscan={5}
              hasMore={hasMoreChats}
              isLoading={isLoadingMoreChats}
              onLoadMore={loadMoreChats}
              className="virtual-chat-list"
              renderItem={(chat, index) => (
                <div
                  key={chat.id}
                  className={`chat-history-item ${selectedChat?.id === chat.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedChat(chat);
                    setMessages([]);
                  }}
                >
                  <div className="chat-history-item-content">
                    <h3>{chat.lastMessage || 'New Chat'}</h3>
                  </div>
                  <button 
                    className="delete-chat-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteChat(chat.id);
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>
              )}
            />
          )}
        </div>
        </div>
      </div>

      <section className="agentv-charts-section">
        <div className="agentv-charts-header">
          <div>
            <h3>Agent V Charts</h3>
            <p>Pin diagnostics without leaving the assistant.</p>
          </div>
          <div className="agentv-charts-actions">
            <button
              className="agentv-refresh-chart-button"
              onClick={loadCharts}
              disabled={loadingCharts}
            >
              {loadingCharts ? (
                <>
                  <FaSpinner className="agentv-button-spinner" />
                  Refreshing
                </>
              ) : (
                'Refresh'
              )}
            </button>
            <button 
              className="agentv-create-chart-button"
              onClick={() => setIsChartOverlayOpen(true)}
            >
              <FaPlus /> New Chat
            </button>
          </div>
        </div>
        <div className="wizard-diagnostics-masonry">
          {loadingCharts ? (
            <div className="wizard-loading-state">
              <div className="wizard-loading-spinner">
                <FaSpinner className="wizard-spinner-icon" />
              </div>
              <p className="wizard-loading-text">Loading charts...</p>
            </div>
          ) : charts.length === 0 ? (
            <div className="wizard-empty-state">
              <div className="wizard-empty-placeholder">
                <span className="wizard-empty-icon">📊</span>
                <p>No charts yet. Generate one with Agent V.</p>
              </div>
            </div>
          ) : (
            charts.map((chart) => (
              <div key={chart.id} className="wizard-chart-item">
                <ChartDisplay 
                  chartData={chart.chartData} 
                  chartId={chart.id}
                  gridPosition={chart.gridPosition}
                  onDelete={handleChartDelete}
                  onUpdate={handleChartUpdate}
                />
              </div>
            ))
          )}
        </div>
      </section>

      <AgentVChatOverlay 
        isOpen={isChartOverlayOpen}
        onClose={() => setIsChartOverlayOpen(false)}
        onChartGenerated={handleChartGenerated}
      />
    </div>
  );
}

// Main Team Plan App Component - This is what gets imported by App.js
function AgentVTeamApp() {
  return <AgentVTeamDashboard />;
}

export default AgentVTeamApp; 